import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  createClaimInvoices,
  deleteClaimInvoice,
  getClaimInvoices,
  refreshClaimInvoice,
  updateClaimInvoice,
} from '@/services/asicw/claim-invoices.service'
import type { IClaimInvoiceFilters, IUpdateInvoiceForm } from '@/types/asicw/invoices'

import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

export const useClaimInvoices = () => {
  const queryClient = useQueryClient()

  const getClaimInvoicesQuery = (filters: IClaimInvoiceFilters) =>
    useQuery({
      queryKey: ['claims', 'invoices', filters],
      queryFn: () => getClaimInvoices(filters),
      placeholderData: (prev: any) => prev,
    })

  const deleteClaimInvoiceMutation = useMutation({
    mutationFn: (invoiceId: number) => {
      return deleteClaimInvoice(invoiceId)
    },
    onSuccess: () => {
      NotifySuccess(`Invoice deleted successfully.`)
      queryClient.invalidateQueries({ queryKey: ['claims', 'invoices'] })
    },
    onError: NotifyErrorWrapper('Failed to delete invoice'),
  })

  const updateClaimInvoiceMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: IUpdateInvoiceForm }) => updateClaimInvoice(id, data),
    onSuccess: () => {
      NotifySuccess('Invoice updated successfully.')
      queryClient.invalidateQueries({ queryKey: ['claims', 'invoices'] })
    },
    onError: NotifyErrorWrapper('Failed to update invoice'),
  })

  const refreshClaimInvoiceMutation = useMutation({
    mutationFn: (invoiceId: number) => {
      return refreshClaimInvoice(invoiceId)
    },
    onSuccess: () => {
      NotifySuccess(`Invoice refreshed successfully.`)
      queryClient.invalidateQueries({ queryKey: ['claims', 'invoices'] })
    },
    onError: NotifyErrorWrapper('Failed to refresh invoice'),
  })

  const createClaimInvoiceMutation = useMutation({
    mutationFn: createClaimInvoices,
    onSuccess: () => {
      NotifySuccess(`Invoice created successfully.`)
      queryClient.invalidateQueries({ queryKey: ['claims', 'invoices'] })
    },
    onError: NotifyErrorWrapper('Failed to create invoice'),
  })

  return {
    getClaimInvoicesQuery,
    deleteClaimInvoiceMutation,
    updateClaimInvoiceMutation,
    refreshClaimInvoiceMutation,
    createClaimInvoiceMutation,
  }
}
